import { type CSSProperties } from 'react';
import { cn } from '@/components/ui/cn';
import { STATE_COLOR, STATE_COLOR_MS, STATE_SIZE } from './companionTheme';
import type { ObserverGesture, ObserverMotion, ObserverPresence, ObserverState } from './observerContext';

/*
  Тело компаньона — чистая внешность без движка (стили: styles/companion.css).

  Получает состояние, жест, размер и направление взгляда, а рисует три вещи: сплошное тело
  с объёмом, ауру снаружи и блик-точку. Ничего внутри корпуса: на малом размере читаются
  только силуэт, размер, аура и цвет (docs/observer-design.md §4.6).

  Размер по состоянию задаёт машина состояний: покой 26, работа 32, долгая операция 48.
*/

export type CompanionGesture = ObserverGesture | 'squash' | 'swell' | 'blink';

interface CompanionBodyProps {
  state: ObserverState;
  gesture?: CompanionGesture | null;
  /** Диаметр в пикселях; по умолчанию — по состоянию. */
  size?: number;
  /** Направление взгляда, −1…1 по каждой оси. */
  look?: { x: number; y: number };
  motion?: ObserverMotion;
  presence?: ObserverPresence;
  className?: string;
}

export function CompanionBody({
  state,
  gesture = null,
  size,
  look = { x: 0, y: 0 },
  motion = 'pronounced',
  presence = 'companion',
  className,
}: CompanionBodyProps) {
  const px = size ?? STATE_SIZE[state];
  return (
    <div
      data-companion=""
      data-state={state}
      data-gesture={gesture ?? undefined}
      data-motion={motion}
      data-presence={presence}
      aria-hidden
      style={
        {
          width: px,
          height: px,
          '--body': STATE_COLOR[state],
          '--color-ms': `${STATE_COLOR_MS[state]}ms`,
          '--look-x': look.x.toFixed(2),
          '--look-y': look.y.toFixed(2),
        } as CSSProperties
      }
      className={cn('relative shrink-0', className)}
    >
      <span data-part="pose">
        <span data-part="aura" />
        <span data-part="gesture">
          <span data-part="skin" />
          <span data-part="shade" />
          <span data-part="glint" />
        </span>
      </span>
    </div>
  );
}
